import { Sheet, SheetStack } from "@silk-hq/components"
import PropTypes from "prop-types"
import React, { createContext, useContext, useState } from "react"
import { createPortal } from "react-dom"

import Button from "src/components/Button"

// Every SideSheet joins one SheetStack: Stack starts one for several sheets,
// a lone Root starts its own, and one opened inside a sheet joins its stack.
const InStackContext = createContext(false)
const SheetContext = createContext({ open: false, close: () => {} })
// false outside a sheet; inside one, the header element its actions render into
// (null for the first render, until the header has mounted).
const HeaderSlotContext = createContext(false)

const useInSideSheet = () => useContext(HeaderSlotContext) !== false

// A modal opened from a sheet (a confirmation, say) sits outside it, and focus
// can stay in the sheet while it's open, so its Escape or a click on it would
// otherwise also dismiss the sheet beneath.
const keepOpenUnderModals = (event) =>
  event.changeDefault({
    dismiss: !document.querySelector('[data-design-system="Modal"]'),
  })

// Puts a screen's own actions in the sheet's header, ahead of the full-page
// link and the close button, so they stay in reach while the body scrolls.
const HeaderActions = ({ children }) => {
  const slot = useContext(HeaderSlotContext)
  if (!slot) return null

  return createPortal(
    <>
      {children}
      <span aria-hidden="true" className="mx-1 h-6 w-px bg-gray-200" />
    </>,
    slot
  )
}

HeaderActions.propTypes = {
  children: PropTypes.node.isRequired,
}

// React bubbles events out of a portal to the sheet's React parents, so a
// click anywhere in the sheet (its content, its backdrop, a sheet stacked on
// it) would also reach whatever the sheet is rendered in, such as a clickable
// table row. Stopping it here keeps every sheet in the stack self-contained.
const Stack = ({ children }) => (
  <SheetStack.Root asChild>
    <span className="contents" onClick={(event) => event.stopPropagation()}>
      <InStackContext.Provider value>{children}</InStackContext.Provider>
    </span>
  </SheetStack.Root>
)

Stack.propTypes = {
  children: PropTypes.node.isRequired,
}

const Root = ({ children, open, onOpenChange }) => {
  const inStack = useContext(InStackContext)
  const sheet = (
    <Sheet.Root
      license="commercial"
      presented={open}
      onPresentedChange={onOpenChange}
      forComponent="closest"
    >
      <InStackContext.Provider value>
        <SheetContext.Provider
          value={{ open, close: () => onOpenChange(false) }}
        >
          {children}
        </SheetContext.Provider>
      </InStackContext.Provider>
    </Sheet.Root>
  )

  return inStack ? sheet : <Stack>{sheet}</Stack>
}

Root.propTypes = {
  children: PropTypes.node.isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
}

// Silk renders nothing for a closed sheet, so its body fetches nothing until it
// opens. onClosed fires once a closed sheet has finished sliding out.
const Content = ({ title, fullPageHref, onClosed, children }) => {
  const { open, close } = useContext(SheetContext)
  const [headerSlot, setHeaderSlot] = useState(null)

  return (
    <Sheet.Portal>
      <Sheet.View
        className="z-modal"
        contentPlacement="right"
        swipeDismissal
        nativeEdgeSwipePrevention
        onClickOutside={keepOpenUnderModals}
        onEscapeKeyDown={keepOpenUnderModals}
        onTravelStatusChange={(status) => {
          // Silk also reports idleOutside on mount, before an open sheet enters.
          if (status === "idleOutside" && !open) onClosed?.()
        }}
      >
        <Sheet.Backdrop
          className="fixed inset-0 bg-black/40"
          travelAnimation={{ opacity: [0, 1] }}
        />
        <Sheet.Content
          data-design-system="SideSheet"
          className="flex h-full w-full flex-col bg-white shadow-xl lg:w-[min(960px,60vw)]"
          stackingAnimation={{ translateX: ["0px", "-24px"] }}
        >
          <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
            <Sheet.Title className="m-0 text-base font-semibold text-gray-900">
              {title}
            </Sheet.Title>
            <div className="flex items-center gap-2">
              <div ref={setHeaderSlot} className="contents" />
              {fullPageHref && (
                <a
                  href={fullPageHref}
                  title="Open full page"
                  aria-label="Open full page"
                  className="btn-ghost-tertiary btn-icon-only inline-flex items-center justify-center"
                >
                  <i
                    className="fa-solid fa-arrow-up-right-from-square"
                    aria-hidden="true"
                  />
                </a>
              )}
              <Button
                variant="ghost-tertiary"
                icon="fa-xmark"
                iconStyle="solid"
                iconOnly
                title="Close"
                onClick={close}
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto py-4">
            <HeaderSlotContext.Provider value={headerSlot}>
              {children}
            </HeaderSlotContext.Provider>
          </div>
        </Sheet.Content>
      </Sheet.View>
    </Sheet.Portal>
  )
}

Content.propTypes = {
  title: PropTypes.node.isRequired,
  fullPageHref: PropTypes.string,
  onClosed: PropTypes.func,
  children: PropTypes.node,
}

const SideSheet = { Stack, Root, Content, HeaderActions, useInSideSheet }

export default SideSheet
